import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useWizard } from '../../context/WizardContext';
import { useLanguage } from '../../context/LanguageContext';
import WizardService from '../../services/wizardService';
import DynamicQuestions from './DynamicQuestions';
import FinancialTooltip from './FinancialTooltip';
import { TOTAL_WIZARD_STEPS } from '../../constants/wizardSteps';

/**
 * CountrySelectionStep component for wizard country selection
 * @param {Object} props - Component props
 * @param {number} props.stepNumber - Current step number in wizard flow
 * @param {string} props.title - Step title displayed to user
 * @param {string} props.description - Optional description text
 * @param {Function} props.onNext - Callback when user proceeds to next step
 * @param {Function} props.onPrevious - Callback when user goes to previous step
 * @param {boolean} props.canGoNext - Whether next button should be enabled
 * @param {boolean} props.canGoPrevious - Whether previous button should be enabled
 * @param {string} props.className - Additional CSS classes
 */
const CountrySelection = ({
  stepNumber = 2,
  totalSteps = TOTAL_WIZARD_STEPS,
  title = "",
  description = "",
  onNext,
  onPrevious,
  canGoNext = false,
  canGoPrevious = true,
  className = ''
}) => {
  const { state, actions } = useWizard();
  const { t } = useLanguage();
  const [tooltipVisible, setTooltipVisible] = useState(false);

  // Auto-scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  }, []);

  // Use translations with fallbacks
  const stepTitle = title || t('wizard.select_country', 'Select Your Destination Country');
  const stepDescription = description || t('wizard.select_country_description', 'Choose the country where you want to immigrate and we\'ll provide relevant guidance based on your preferences.');

  const selectedCountry = state.countrySelection.selectedCountry;
  const answers = state.countrySelection.answers;

  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);

  const quickFacts = useMemo(() => ([
    {
      label: t('wizard.country.quickFacts.time.label', 'Average completion time'),
      value: t('wizard.country.quickFacts.time.value', '≈ 6 minutes'),
      description: t('wizard.country.quickFacts.time.description', 'Finish the wizard once and our advisors arrive prepared for your consultation.')
    },
    {
      label: t('wizard.country.quickFacts.docs.label', 'Documentation saved'),
      value: t('wizard.country.quickFacts.docs.value', '30+ mins'),
      description: t('wizard.country.quickFacts.docs.description', 'We pre-fill onboarding paperwork using the answers you share right now.')
    },
    {
      label: t('wizard.country.quickFacts.pathways.label', 'Personalised pathways'),
      value: t('wizard.country.quickFacts.pathways.value', '3 curated options'),
      description: t('wizard.country.quickFacts.pathways.description', 'Unlock tailored study or work routes for your profile before the first call.')
    }
  ]), [t]);

  const countryGuidance = useMemo(() => ({
    germany: {
      title: t('wizard.country.guidance.germany.title', 'What to expect for Germany'),
      bullets: [
        t('wizard.country.guidance.germany.bullet1', 'English-taught Bachelor & Master programmes with €0 tuition at public universities.'),
        t('wizard.country.guidance.germany.bullet2', 'Chancenkarte and Skilled Worker pathways: points-based assessment with rapid job market support.'),
        t('wizard.country.guidance.germany.bullet3', 'Blocked account guidance and bank partners shared immediately after this step.')
      ]
    },
    canada: {
      title: t('wizard.country.guidance.canada.title', 'What to expect for Canada'),
      bullets: [
        t('wizard.country.guidance.canada.bullet1', 'PG Diploma funding options with 25% upfront payment and 75% after arrival financing.'),
        t('wizard.country.guidance.canada.bullet2', 'PGWP timelines and province-specific admission windows included in your personalised pack.'),
        t('wizard.country.guidance.canada.bullet3', 'Work while studying roadmap with average earnings by province.')
      ]
    },
    uk: {
      title: t('wizard.country.guidance.uk.title', 'What to expect for the United Kingdom'),
      bullets: [
        t('wizard.country.guidance.uk.bullet1', 'One-year Master programmes with Graduate Route extension details.'),
        t('wizard.country.guidance.uk.bullet2', 'Scholarship shortlists and CAS processing timelines prepared automatically.'),
        t('wizard.country.guidance.uk.bullet3', 'Visa success checklist covering maintenance funds and accommodation proof.')
      ]
    },
    newzealand: {
      title: t('wizard.country.guidance.newzealand.title', 'What to expect for New Zealand'),
      bullets: [
        t('wizard.country.guidance.newzealand.bullet1', 'Bachelor & Master programmes with work Visa and study-to-residency migration support.'),
        t('wizard.country.guidance.newzealand.bullet2', 'Post-study work and residency pathways available with high success rates.'),
        t('wizard.country.guidance.newzealand.bullet3', 'Beautiful environment with excellent quality of life and work-life balance.')
      ]
    },
    notSure: {
      title: t('wizard.country.guidance.notSure.title', 'Need help deciding?'),
      bullets: [
        t('wizard.country.guidance.notSure.bullet1', 'Flight Plan™ Alliance: Apply to ALL 5 countries (Germany, Canada, Australia, UK, New Zealand) with one profile.'),
        t('wizard.country.guidance.notSure.bullet2', 'Get a career-aligned shortlist with time-to-visa and investment required.'),
        t('wizard.country.guidance.notSure.bullet3', 'Receive a prep pack covering language, budget, and documentation expectations.')
      ]
    }
  }), [t]);

  useEffect(() => {
    loadCountries();
  }, []);

  const loadCountries = async () => {
    try {
      setLoading(true);
      const countriesData = await WizardService.getCountries();
      setCountries(countriesData);
    } catch (error) {
      console.error('Error loading countries:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCountrySelect = useCallback((countryCode) => {
    const selectedCountry = countries.find(c => c.code === countryCode);
    const questions = selectedCountry?.questions || [];
    actions.updateCountrySelection({
      selectedCountry: countryCode,
      dynamicQuestions: questions,
      answers: {} // Reset answers when country changes
    });

    // Show tooltip briefly when country is selected
    setTooltipVisible(true);
    setTimeout(() => setTooltipVisible(false), 5000);
  }, [actions, countries]);

  const handleAnswer = useCallback((questionId, answer) => {
    actions.updateCountrySelection({
      answers: {
        ...answers,
        [questionId]: answer
      }
    });
  }, [actions, answers]);

  const handleNext = useCallback(() => {
    if (selectedCountry && onNext) {
      onNext({
        countrySelection: state.countrySelection
      });
    }
  }, [selectedCountry, state.countrySelection, onNext]);

  const canProceed = useMemo(() => {
    if (!selectedCountry) {
      return false;
    }

    const selectedCountryData = countries.find(c => c.code === selectedCountry);
    const questions = selectedCountryData?.questions || [];

    const allAnswered = questions.every(q => answers[q.id]);

    return allAnswered;
  }, [selectedCountry, countries, answers]);

  const selectedCountryMeta = useMemo(() => {
    if (!selectedCountry) {
      return {
        title: t('wizard.country.guidance.default.title', 'How we use your choice'),
        bullets: [
          t('wizard.country.guidance.default.bullet1', 'We tailor every upcoming question to the immigration rules of the country you select.'),
          t('wizard.country.guidance.default.bullet2', 'Visa requirements, costs, and timelines are pre-loaded so you only see relevant guidance.'),
          t('wizard.country.guidance.default.bullet3', 'You can switch countries at any time before submission—your answers will update automatically.')
        ]
      };
    }

    return countryGuidance[selectedCountry] || {
      title: t('wizard.country.guidance.fallback.title', 'What to expect next'),
      bullets: [
        t('wizard.country.guidance.fallback.bullet1', 'We summarise visa requirements, tuition or salary expectations, and next steps for your advisor.'),
        t('wizard.country.guidance.fallback.bullet2', 'You will receive downloadable preparation guides immediately after submission.'),
        t('wizard.country.guidance.fallback.bullet3', 'Our specialists review your profile before calling so you can jump straight into planning.')
      ]
    };
  }, [selectedCountry, countryGuidance, t]);

  return (
    <div className={`country-selection-step w-full mx-auto ${className}`}>
      {/* Header */}
      <div className="text-center mb-12 animate-fade-in-up">
        <div className="mb-6">
          <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 border border-blue-200 shadow-sm">
            <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            {t('wizard.common.stepIndicator', 'Step {{current}} of {{total}}', {
              current: stepNumber,
              total: totalSteps
            })}
          </span>
        </div>
        <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4 animate-fade-in-up" style={{animationDelay: '0.1s'}}>
          {stepTitle}
        </h2>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto animate-fade-in-up" style={{animationDelay: '0.2s'}}>
          {stepDescription}
        </p>
      </div>

      {/* Country Selection Grid */}
      {loading ? (
        <div className="flex flex-col justify-center items-center h-80 animate-fade-in-scale">
          <div className="spinner mb-4"></div>
          <p className="text-gray-600 font-medium">Loading countries...</p>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6 w-full">
            {[1, 2, 3].map((i) => (
              <div key={i} className="country-card">
                <div className="skeleton skeleton-title mb-4"></div>
                <div className="skeleton skeleton-text w-3/4"></div>
                <div className="skeleton skeleton-text w-1/2"></div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6 mb-10 animate-fade-in-up" style={{animationDelay: '0.7s'}}>
          {countries.map((country, index) => (
            <CountryCard
              key={country.code}
              country={country}
              isSelected={selectedCountry === country.code}
              onSelect={() => handleCountrySelect(country.code)}
              animationDelay={index * 0.1}
            />
          ))}
        </div>
      )}

      {/* Dynamic Questions */}
      {selectedCountry && (
        <div className="dynamic-questions-section mb-10 animate-fade-in-scale">
          <DynamicQuestions
            questions={state.countrySelection.dynamicQuestions}
            answers={answers}
            onAnswer={handleAnswer}
          />
        </div>
      )}

      {/* Financial Tooltip */}
      {selectedCountry && (
        <div className="financial-tooltip-section mb-10 animate-fade-in-scale">
          <FinancialTooltip
            countryCode={selectedCountry}
            visible={tooltipVisible}
            onToggle={() => setTooltipVisible(!tooltipVisible)}
          />
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between items-center mt-12 pt-8 border-t border-gray-200 animate-fade-in-up" style={{animationDelay: '0.8s'}}>
        <button
          onClick={onPrevious}
          disabled={!canGoPrevious}
          className="btn-modern hover-scale disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:transform-none"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          {t('common.previous', 'Previous')}
        </button>

        <button
          onClick={handleNext}
          disabled={!canProceed}
          className="btn-modern btn-primary hover-lift disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:transform-none disabled:hover:shadow-none"
        >
          {t('common.next', 'Next Step')}
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
  );
};

/**
 * CountryCard component for individual country selection
 */
const CountryCard = ({ country, isSelected, onSelect, animationDelay = 0 }) => {
  return (
    <div
      onClick={onSelect}
      className={`country-card hover-lift animate-fade-in-up ${
        isSelected ? 'selected' : ''
      }`}
      style={{animationDelay: `${animationDelay}s`}}
      role="button"
      tabIndex={0}
      onKeyPress={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
      aria-pressed={isSelected}
      aria-label={`Select ${country.name}`}
    >
      {isSelected && (
        <div className="absolute top-4 right-4 animate-fade-in-scale">
          <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center shadow-lg">
            <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </div>
        </div>
      )}

      <div className="text-center">
        <div className="text-5xl mb-4 transform transition-transform hover:scale-110">{country.flag}</div>
        <h3 className="text-xl font-bold text-gray-900 mb-3">{country.name}</h3>
        <div className="flex flex-wrap gap-2 justify-center">
          {country.services.slice(0, 3).map((service, index) => (
            <span
              key={index}
              className="px-3 py-1.5 text-xs font-semibold bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 rounded-full border border-blue-200 shadow-sm"
            >
              {service}
            </span>
          ))}
          {country.services.length > 3 && (
            <span className="px-3 py-1.5 text-xs font-semibold bg-gradient-to-r from-gray-50 to-gray-100 text-gray-600 rounded-full border border-gray-200">
              +{country.services.length - 3} more
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default CountrySelection;
